import { useEffect, useRef, useCallback, useMemo, memo } from 'react'
import { useActiveChatId, useUser } from '@renderer/chat/store'
import { GlobalEvents, GlobalState } from '@highlight/ipc'
import { useGlobalEvent } from '@highlight/ipc/react'
import { useAtomValue, useSetAtom } from 'jotai'
import { AmplitudeRenderer } from '@renderer/utils/amplitude.renderer'
import { stagedActionEventProperties } from '@renderer/overlay/Sidepanel/hooks/staged-actions/track-staged-action'
import { useSidepanelState } from '../atoms'
import { useViewNavigation } from '../Sidepanel/view-atoms'
import { viewContextOf } from '../Sidepanel/view-definitions'
import { useStagedActionJobs } from '@renderer/overlay/hooks/use-staged-action-jobs'
import { useGatewayConnect } from '@renderer/overlay/hooks/use-gateway-connect'
import { useHighlightShells } from '@renderer/providers/highlight-shells-provider'
import {
  getVisibleStagedActions,
  VisibleStagedActionsContext,
  useInvokeStagedAction,
  usePruneHiddenStagedActions,
  useStagedActionDerivations,
  useStagedActionsQuery,
  useStagedActionUndo,
  useArchiveStagedActions,
  useStagedActionReadState,
  usePendingActionExpiry,
  useArchivedActionExpiry,
  forgetExecution,
  type StagedAction,
} from '../Sidepanel/hooks/use-staged-actions'
import { useAutoClearStagedActions } from '../Sidepanel/hooks/use-auto-clear-staged-actions'
import {
  loadAnnouncedIds,
  rememberAnnounced,
  selectFreshItems,
  selectFlushableSuppressedItems,
} from './new-item-detection'
import { StagedActionCallbacksProvider, type StagedActionCallbacks } from '../Sidepanel/hooks/use-staged-action-view'
import { isUnread } from '../Sidepanel/hooks/staged-action-status'
import {
  archivedDisplayAtom,
  manuallyHiddenAtom,
  pendingDisplayAtom,
  undoErrorsAtom,
} from '../Sidepanel/hooks/staged-action-local-state'

// Cap on the new-action dedupe set so it stays bounded across a long overlay
// session. Current actions are always retained (FIFO eviction drops only the
// oldest, least-likely-to-reappear ids).
const MAX_SEEN_ACTION_IDS = 1000

// A single render pass surfacing more than this many unseen actions is a
// reconcile / cache-sync artifact (a window's whole list landing at once), not
// genuine simultaneous news — a real `staged-action:new` arrives one or a few at
// a time. Seed such a burst silently (the unread badge still reflects it) rather
// than spawning a wall of toasts.
const MAX_NEW_ACTION_TOASTS_PER_PASS = 10

/**
 * Single mount-point for the staged-actions query at the overlay root.
 * Its always-on query observer keeps gateway invalidations fetching even when
 * no sidepanel-side consumer is mounted (HIG-5126). Provider re-renders from
 * its subscriptions don't cascade to children, which are passed through as a
 * stable prop. Feeds new-action toasts, stale-action
 * auto-clear, and the callbacks context consumed by every action cell.
 */
export const StagedActionsProvider = memo(({ children }: React.PropsWithChildren) => {
  const user = useUser()
  const { refetch: refetchStagedActionJobs } = useStagedActionJobs()
  const invokeStagedActionWithSource = useInvokeStagedAction()
  const setPendingDisplay = useSetAtom(pendingDisplayAtom)
  const setArchivedDisplay = useSetAtom(archivedDisplayAtom)
  const setUndoErrors = useSetAtom(undoErrorsAtom)
  const setManuallyHidden = useSetAtom(manuallyHiddenAtom)

  const isSignedIn = !!user && !user.isAnonymous
  // `hasSeededRef` flips true once this window's own initial fetch has resolved
  // and the existing backlog has been recorded in `prevIdsRef`. `wasFetchingRef`
  // tracks whether our own queryFn has been in flight: a real fetch cycles
  // isFetching true→false, whereas a cross-window cache-sync (applyRemote) never
  // touches it — letting us tell "my fetch resolved" from "a peer wrote my cache".
  const hasSeededRef = useRef(false)
  const wasFetchingRef = useRef(false)
  const prevIdsRef = useRef(new Set<string>())
  // Fresh unread actions whose toast was suppressed because their conversation
  // was open inline in the sidepanel. Held (not dropped) so they can still
  // announce when the panel closes — see the flush effect below. HIG-6036.
  const suppressedInlineRef = useRef(new Map<string, StagedAction>())

  const { allActions, hasFetched, isLoading, isFetching, refetch: refetchStagedActions } = useStagedActionsQuery()
  const { getActionStatus } = useStagedActionDerivations()
  const manuallyHidden = useAtomValue(manuallyHiddenAtom)
  const archivedDisplay = useAtomValue(archivedDisplayAtom)
  const { shells } = useHighlightShells()
  const liveShellIds = useMemo(() => new Set(shells.map((s) => s.id)), [shells])
  const visibleActions = useMemo(
    () => getVisibleStagedActions({ allActions, manuallyHidden, liveShellIds, getActionStatus, archivedDisplay }),
    [allActions, manuallyHidden, liveShellIds, getActionStatus, archivedDisplay],
  )
  const visibleActionsValue = useMemo(
    () => ({ stagedActions: visibleActions, allActions, isLoading, isFetching, hasFetched }),
    [visibleActions, allActions, isLoading, isFetching, hasFetched],
  )
  const invokeStagedActionButton = useCallback(
    (id: string) => invokeStagedActionWithSource(id, { source: 'button' }),
    [invokeStagedActionWithSource],
  )
  const { undoStagedAction } = useStagedActionUndo()
  const { bulkArchiveStagedActions, archiveStagedActionWithUndo, undoArchiveStagedAction } = useArchiveStagedActions()
  const { markActionExpired, markActionAsRead } = useStagedActionReadState()

  usePruneHiddenStagedActions(allActions, hasFetched)
  useAutoClearStagedActions({ allActions, bulkArchiveStagedActions })
  // Expire window-local pending markers here, co-located with the seeding (the
  // peer-invoke and external-cc invoke handlers below). This provider is the
  // always-mounted owner in every window that seeds pendingDisplay (overlay and
  // chat window), so markers can't get stuck pending when the sidepanel chat
  // input isn't mounted (e.g. the chat window).
  usePendingActionExpiry()
  useArchivedActionExpiry()

  const actionCallbacks = useMemo<StagedActionCallbacks>(
    () => ({
      invokeStagedAction: invokeStagedActionButton,
      undoStagedAction,
      bulkArchiveStagedActions,
      archiveStagedActionWithUndo,
      undoArchiveStagedAction,
      markActionExpired,
      markActionAsRead,
    }),
    [
      invokeStagedActionButton,
      undoStagedAction,
      bulkArchiveStagedActions,
      archiveStagedActionWithUndo,
      undoArchiveStagedAction,
      markActionExpired,
      markActionAsRead,
    ],
  )

  // Track both the view context chatId and the live activeChatId to suppress
  // action toasts for the conversation currently rendered inline. During a
  // temp→real ID transition the context.chatId lags behind activeChatId by a
  // render cycle, so checking both prevents redundant toasts when the action
  // arrives with the real conversationId. HIG-5126.
  const { topView: top } = useViewNavigation()
  const isTopChatView = top.id === 'chat'
  const topChatId = viewContextOf(top, 'chat')?.chatId
  const liveActiveChatId = useActiveChatId()
  const activeChatViewIdRef = useRef<{ contextId?: string; liveId?: string }>({})
  useEffect(() => {
    activeChatViewIdRef.current = isTopChatView ? { contextId: topChatId, liveId: liveActiveChatId } : {}
  }, [isTopChatView, topChatId, liveActiveChatId])

  // Reconcile on every gateway (re)connect. Realtime delivery is best-effort —
  // the server publishes `staged-actions` updates over fire-and-forget pub/sub
  // with no replay, so a broadcast is lost across any WS drop. The common case:
  // a long-running job (>10min) with the sidepanel closed leaves the gateway
  // connection idle, the gateway DO reaps it for stale auth, and the
  // action-created broadcast that fires at the end is dropped — so no toast and
  // a stale list until something forces a refetch. Refetching the actions list
  // here (not just jobs) lets a missed `updated` still surface: the new-action
  // detector below sees the id and toasts it. HIG-5221.
  useGatewayConnect(
    async () => {
      if (!isSignedIn) {
        return
      }

      await Promise.all([
        refetchStagedActionJobs().catch((error) => {
          console.error('Failed to refetch staged action jobs on gateway reconnect:', error)
        }),
        refetchStagedActions().catch((error) => {
          console.error('Failed to refetch staged actions on gateway reconnect:', error)
        }),
      ])
    },
    { enabled: isSignedIn },
  )

  // Detect genuinely new actions (toast + analytics) from the raw query data.
  useEffect(() => {
    // Note our own fetch was in flight. Must run before the hasFetched guard so
    // the initial fetch (data still undefined) is recorded.
    if (isFetching) wasFetchingRef.current = true

    // Wait for real data — `hasFetched` is data !== undefined.
    if (!hasFetched) return

    const validActions = allActions.filter((a): a is StagedAction => !!a && typeof a.id === 'string')
    const currentIds = new Set(validActions.map((a) => a.id))

    // Accumulate seen ids (don't replace) so an action that transiently drops
    // out of a fetch — an optimistic-removal rollback, or a list-endpoint
    // omission during a refetch race — and later reappears is NOT re-toasted
    // and re-counted as new. Bound with FIFO eviction so the set can't grow
    // unbounded across a multi-day overlay session.
    const recordSeen = () => {
      for (const id of currentIds) prevIdsRef.current.add(id)
      if (prevIdsRef.current.size > MAX_SEEN_ACTION_IDS) {
        prevIdsRef.current = new Set(Array.from(prevIdsRef.current).slice(-MAX_SEEN_ACTION_IDS))
      }
    }

    // Seed the existing backlog silently (no toasts, no analytics) until the
    // list has settled. `hasFetched` can flip true from a peer cross-window
    // cache-sync (applyRemote → setQueryData) before our own queryFn returns —
    // e.g. a peer's `setQueryData(old => old ?? [])` broadcasts an empty/partial
    // list. Seeding on that pass and toasting on the next would surface the whole
    // backlog as "new". Treat the list as settled (and start toasting) once we're
    // not mid-fetch AND either our own fetch has cycled (covers the genuinely-
    // empty account, so its first real action still toasts) or we already hold a
    // non-empty list (covers a fresh-cache mount or instant fetch that never
    // emits isFetching — without this the detector would stay seeding forever and
    // never toast).
    if (!hasSeededRef.current) {
      recordSeen()
      if (!isFetching && (wasFetchingRef.current || validActions.length > 0)) hasSeededRef.current = true
      return
    }

    // Suppress emission for actions whose conversation is currently rendered
    // inline in the chat drawer — those are visible already, and a toast on top
    // would be redundant. HIG-5126.
    const { contextId, liveId } = activeChatViewIdRef.current
    // Only treat the chat drawer as "active" (and thus suppress) when the
    // sidepanel is actually open. When it's hidden (e.g. backgrounded) the
    // inline action isn't visible, so the toast must still fire. HIG-5221.
    const sidepanelOpen = GlobalState.get<string>('sidepanel.state', 'hidden') === 'open'

    // Genuinely new = not archived, never announced before, unseen by this
    // window, AND created recently. The archived / already-announced gates are
    // the durable ones (an archive or an edit re-presents a row that looks brand
    // new); the age gate catches a backlog item resurfacing (refetch /
    // cross-window sync / mock inject / reset seen-set), which must neither toast
    // nor emit `generated`. Without them, one such reconcile floods analytics and
    // (via the batch cap) even suppresses a legitimately-new action arriving in
    // the same pass. See `new-item-detection.ts`.
    const freshActions = selectFreshItems({
      items: validActions,
      seenIds: prevIdsRef.current,
      announcedIds: loadAnnouncedIds('stagedAction'),
      now: Date.now(),
      isRetired: (action) => action.archivedAt != null,
    })

    // A burst of many fresh actions in one pass is still treated as a reconcile
    // artifact — seed it silently rather than spawning a wall of toasts (the
    // unread badge still reflects it).
    const toastBatch = freshActions.length <= MAX_NEW_ACTION_TOASTS_PER_PASS

    for (const action of freshActions) {
      const inActiveChatView =
        (!!contextId && action.conversationId === contextId) || (!!liveId && action.conversationId === liveId)
      // Gate the toast on durable server seen-state (readAt) too: an unread,
      // recently-created action this window hasn't toasted should toast, but a
      // read one must not.
      // An action replacing a planner placeholder was announced as that placeholder.
      if (toastBatch && isUnread(action) && !action.metadata?.replaces) {
        if (inActiveChatView && sidepanelOpen) {
          // Visible inline right now — hold it so it can still announce if the
          // panel closes before the user deals with it (HIG-6036). The flush
          // effect below re-validates and emits when the panel leaves 'open'.
          // Not rememberAnnounced: only an actual toast consumes the
          // announcement.
          suppressedInlineRef.current.set(action.id, action)
        } else {
          GlobalEvents.emit('staged-action:new', action)
          rememberAnnounced('stagedAction', action.id)
        }
      }
      AmplitudeRenderer.trackEvent('Staged Action', {
        action: 'generated',
        ...stagedActionEventProperties(action),
        // The only link back to the meeting or chat that produced the action.
        conversationId: action.conversationId,
      })
    }

    recordSeen()
  }, [allActions, hasFetched, isFetching])

  // Flush inline-suppressed actions when the sidepanel leaves 'open' (HIG-5248
  // shape: skip-but-don't-consume, fire on close). A dedicated effect rather
  // than a detector dep so panel transitions don't re-run the whole diff and
  // re-fire 'generated' analytics. Suppression only happens while 'open', so
  // any departure ('hidden' or 'peeking') flushes.
  const sidepanelState = useSidepanelState()
  const prevSidepanelStateRef = useRef(sidepanelState)
  const allActionsRef = useRef(allActions)
  useEffect(() => {
    allActionsRef.current = allActions
  }, [allActions])

  useEffect(() => {
    const wasOpen = prevSidepanelStateRef.current === 'open'
    prevSidepanelStateRef.current = sidepanelState
    if (!wasOpen || sidepanelState === 'open' || suppressedInlineRef.current.size === 0) return

    const pending = Array.from(suppressedInlineRef.current.values())
    suppressedInlineRef.current.clear()
    const latestById = new Map<string, StagedAction>()
    for (const action of allActionsRef.current) {
      if (action && typeof action.id === 'string') latestById.set(action.id, action)
    }
    // Re-validate against the freshest row: the user may have read or archived
    // the action inline, or a peer window may have announced it, while held.
    // The age gate deliberately doesn't re-apply — the hold was user-caused.
    const flushable = selectFlushableSuppressedItems({
      pending,
      latestById,
      announcedIds: loadAnnouncedIds('stagedAction'),
      isRetired: (action) => action.archivedAt != null,
      isUnread,
      max: MAX_NEW_ACTION_TOASTS_PER_PASS,
    })
    for (const action of flushable) {
      GlobalEvents.emit('staged-action:new', action)
      rememberAnnounced('stagedAction', action.id)
    }
  }, [sidepanelState])

  // Listen for invoke requests from the main process (e.g. external CC sessions).
  // This must live here (mounted once) rather than in a per-consumer hook
  // to avoid firing duplicate invoke requests. The invoke flow itself is shared with the
  // in-app button via useInvokeStagedAction so both paths produce identical state.
  useGlobalEvent(
    'staged-action:invoke',
    useCallback(
      (stagedActionId: string, meta?: { external?: boolean; claudeCodeSessionId?: string }) => {
        void invokeStagedActionWithSource(stagedActionId, {
          source: 'external-cc',
          claudeCodeSessionId: meta?.claudeCodeSessionId,
        })
      },
      [invokeStagedActionWithSource],
    ),
  )

  // A peer window invoked an action. Its synced cache write already mirrored the
  // outputData/lastInvokedAt here, but the pending marker is window-local — so
  // without this, this window derives "completed" and hides the row under the
  // completed filter while the invoking window shows it pending. Seed the same
  // marker (the local pending-expiry timer clears it on the shared 60s schedule).
  useGlobalEvent(
    'staged-action:invoked',
    useCallback(
      (stagedActionId: string, startTime: number) => {
        setPendingDisplay((prev) => {
          if (prev.has(stagedActionId)) return prev
          const next = new Map(prev)
          next.set(stagedActionId, startTime)
          return next
        })
      },
      [setPendingDisplay],
    ),
  )

  // A peer window undid an action. Its synced cache write already reverted the
  // row here, but invoke/undo status is gated by window-local atoms — if *this*
  // window invoked the action it still holds a pending marker that shadows the
  // cache and pins the cell to "pending". Drop the stale markers so the cell
  // falls through to the cache-derived staged state. (Counterpart to the
  // emitToPeers in useStagedActionUndo's onMutate.)
  useGlobalEvent(
    'staged-action:reverted',
    useCallback(
      (stagedActionId: string) => {
        // Mirror the undoing window's `forgetExecution`: this window may hold an
        // execution mark of its own (it invoked the action), which would restore
        // the reverted result on the next fetch.
        forgetExecution(stagedActionId)
        setPendingDisplay((prev) => {
          if (!prev.has(stagedActionId)) return prev
          const next = new Map(prev)
          next.delete(stagedActionId)
          return next
        })
        setUndoErrors((prev) => {
          if (!prev.has(stagedActionId)) return prev
          const next = new Map(prev)
          next.delete(stagedActionId)
          return next
        })
        // Mirror the undoing window: an action expired into manuallyHidden here
        // must un-hide so the reverted, now-staged row reappears in this window too.
        setManuallyHidden((prev) => {
          if (!prev.has(stagedActionId)) return prev
          const next = new Set(prev)
          next.delete(stagedActionId)
          return next
        })
      },
      [setPendingDisplay, setUndoErrors, setManuallyHidden],
    ),
  )

  // A peer window archived an action with the undo window. The synced cache
  // write already carries archivedAt here; seed the same display marker so this
  // window's list lingers the row (with its countdown) instead of dropping it.
  useGlobalEvent(
    'staged-action:archived',
    useCallback(
      (stagedActionId: string, startTime: number) => {
        setArchivedDisplay((prev) => {
          if (prev.has(stagedActionId)) return prev
          const next = new Map(prev)
          next.set(stagedActionId, startTime)
          return next
        })
      },
      [setArchivedDisplay],
    ),
  )

  // A peer window took an archive back — drop the marker so this window's row
  // returns to plain staged rendering instead of showing a stale countdown.
  useGlobalEvent(
    'staged-action:archive-reverted',
    useCallback(
      (stagedActionId: string) => {
        setArchivedDisplay((prev) => {
          if (!prev.has(stagedActionId)) return prev
          const next = new Map(prev)
          next.delete(stagedActionId)
          return next
        })
      },
      [setArchivedDisplay],
    ),
  )

  return (
    <VisibleStagedActionsContext.Provider value={visibleActionsValue}>
      <StagedActionCallbacksProvider value={actionCallbacks}>{children}</StagedActionCallbacksProvider>
    </VisibleStagedActionsContext.Provider>
  )
})
StagedActionsProvider.displayName = 'StagedActionsProvider'
